import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, CheckCircle2, Loader2, MessageCircle, Check } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useSiteContent } from "../../context/SiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const COUNTRY_CODES = [
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+49", label: "🇩🇪 +49" },
  { code: "+966", label: "🇸🇦 +966" },
  { code: "+27", label: "🇿🇦 +27" },
  { code: "+234", label: "🇳🇬 +234" },
];

const INSTALL_TYPES = ["Ground-Mounted Solar Park", "Commercial & Industrial Rooftop", "Floating Solar"];
const CLEAN_PREFS = ["100% Waterless Dry Cleaning", "Hybrid Wet & Dry Cleaning", "Annual O&M Contract"];
const ROBOT_LINES = ["Yantr 2.0 Automatic", "Yantr 2.0 Semi-Auto", "Yantr 3.0 Crawler", "Need Expert Recommendation"];

const STEPS = ["Plant Infrastructure", "Cleaning & Service Scope", "Commercial & Location"];

function OptionGroup({ options, value, onChange, testidPrefix }) {
  return (
    <div className="grid gap-2.5">
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button key={opt} type="button" onClick={() => onChange(opt)}
            data-testid={`${testidPrefix}-${opt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            className={`flex items-center justify-between rounded-xl border px-4 py-3 text-left text-sm font-medium transition-colors ${active ? "border-helios-cyan bg-helios-cyan/5 text-helios-slate" : "border-helios-line bg-white text-slate-600 hover:border-slate-300"}`}>
            {opt}
            <span className={`flex items-center justify-center w-5 h-5 rounded-full border ${active ? "border-helios-cyan bg-helios-cyan text-white" : "border-slate-300"}`}>
              {active && <Check size={13} />}
            </span>
          </button>
        );
      })}
    </div>
  );
}

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="block text-xs font-medium text-slate-500 mb-1.5">{label}</span>
    <Input className="rounded-lg border-helios-line" {...props} />
  </label>
);

const emptyForm = {
  installationType: "",
  plantCapacity: 1000,
  tiltAngle: "",
  rowPitch: "",
  cleaningPreference: "",
  robotLine: "",
  organization: "",
  contactPerson: "",
  countryCode: "+91",
  mobile: "",
  email: "",
  city: "",
  state: "",
};

export const RfqWizard = ({ open, onClose, prefill }) => {
  const { content } = useSiteContent();
  const waNumber = content?.companyInfo?.whatsappNumber || "919998147181";
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [waLink, setWaLink] = useState("");

  const leadType = prefill?.leadType || "quote";
  const heading =
    leadType === "feasibility"
      ? "Custom 10-Year Feasibility Report"
      : leadType === "om"
      ? "Custom O&M Proposal"
      : "Request a Quote";

  useEffect(() => {
    if (open) {
      setStep(1);
      setDone(false);
      setForm({
        ...emptyForm,
        plantCapacity: prefill?.plantCapacity || 1000,
        robotLine: prefill?.robotLine || "",
        cleaningPreference: prefill?.cleaningPreference || "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const set = (k, v) => setForm({ ...form, [k]: v });

  const next = () => {
    if (step === 1 && (!form.installationType || !form.plantCapacity)) {
      return toast.error("Select an installation type and enter plant capacity.");
    }
    if (step === 2 && (!form.cleaningPreference || !form.robotLine)) {
      return toast.error("Select a cleaning preference and robot line.");
    }
    setStep((s) => Math.min(3, s + 1));
  };

  const buildWa = () => {
    const t =
      `Hello Helios Cleantech, I'd like a ${heading}.\n\n` +
      `• Installation: ${form.installationType}\n` +
      `• Plant Capacity: ${form.plantCapacity} kWp\n` +
      (form.tiltAngle ? `• Module Tilt: ${form.tiltAngle}°\n` : "") +
      (form.rowPitch ? `• Inter-row Pitch: ${form.rowPitch}\n` : "") +
      `• Cleaning: ${form.cleaningPreference}\n` +
      `• Robot Line: ${form.robotLine}\n` +
      `• Organization: ${form.organization}\n` +
      `• Contact: ${form.contactPerson} (${form.countryCode} ${form.mobile})\n` +
      `• Email: ${form.email}\n` +
      `• Location: ${form.city}, ${form.state}`;
    return `https://wa.me/${waNumber}?text=${encodeURIComponent(t)}`;
  };

  const submit = async () => {
    if (!form.organization || !form.contactPerson || !form.mobile || !form.email || !form.city || !form.state) {
      return toast.error("Please complete all commercial and location details.");
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.contactPerson,
        email: form.email,
        phone: `${form.countryCode} ${form.mobile}`,
        company: form.organization,
        message: `${heading} — ${form.installationType}, ${form.plantCapacity} kWp`,
        lead_type: leadType,
        roi_snapshot: prefill?.roiSnapshot || null,
        details: {
          "Installation Type": form.installationType,
          "Plant Capacity (kWp)": form.plantCapacity,
          "Module Tilt": form.tiltAngle || "-",
          "Inter-row Pitch": form.rowPitch || "-",
          "Cleaning Preference": form.cleaningPreference,
          "Robot Line": form.robotLine,
          "City": form.city,
          "State": form.state,
        },
      });
      setWaLink(buildWa());
      setDone(true);
      toast.success("Inquiry received. Our engineering team will respond shortly.");
    } catch (e) {
      toast.error("Something went wrong. Please try again or WhatsApp us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} data-testid="rfq-wizard">
          <div className="absolute inset-0 bg-helios-slate/50 backdrop-blur-sm" onClick={onClose} data-testid="rfq-overlay" />
          <motion.div
            className="relative z-10 w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl bg-white border border-helios-line shadow-2xl"
            initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}>
            <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-helios-slate transition-colors z-10" data-testid="rfq-close">
              <X size={20} />
            </button>

            {done ? (
              <div className="p-10 text-center">
                <CheckCircle2 size={52} className="mx-auto text-helios-cyan" />
                <h3 className="font-display text-2xl font-bold mt-5 text-helios-slate">Inquiry received</h3>
                <p className="text-slate-500 mt-2 leading-relaxed max-w-sm mx-auto">
                  Thank you, {form.contactPerson.split(" ")[0] || "there"}. Our team will reach out shortly.
                  Fast-track it now by forwarding the details on WhatsApp.
                </p>
                <a href={waLink} target="_blank" rel="noopener noreferrer"
                  className="btn-sheen mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-600 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
                  data-testid="rfq-whatsapp-forward">
                  <MessageCircle size={17} /> Forward Inquiry via WhatsApp
                </a>
                <div>
                  <button onClick={onClose} className="mt-4 text-sm text-slate-400 hover:text-helios-slate transition-colors" data-testid="rfq-done-close">
                    Close
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-8">
                <p className="overline text-helios-cyan mb-1.5">{heading}</p>
                {/* progress */}
                <div className="flex items-center gap-2 mt-3 mb-6">
                  {STEPS.map((s, i) => (
                    <div key={s} className="flex-1">
                      <div className={`h-1.5 rounded-full transition-colors ${i < step ? "bg-helios-cyan" : "bg-helios-line"}`} />
                      <span className={`mt-2 block text-[10px] font-mono uppercase tracking-wider ${i + 1 === step ? "text-helios-slate" : "text-slate-400"}`}>
                        {i + 1}. {s}
                      </span>
                    </div>
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}>
                    {step === 1 && (
                      <div className="space-y-5">
                        <div>
                          <p className="text-sm font-semibold text-helios-slate mb-3">Installation Type</p>
                          <OptionGroup options={INSTALL_TYPES} value={form.installationType}
                            onChange={(v) => set("installationType", v)} testidPrefix="rfq-install" />
                        </div>
                        <Field label="Plant Capacity (kWp)" type="number" value={form.plantCapacity}
                          onChange={upd("plantCapacity")} data-testid="rfq-capacity" />
                        <div className="grid grid-cols-2 gap-4">
                          <Field label="Module Tilt Angle (°) — optional" type="number" value={form.tiltAngle}
                            onChange={upd("tiltAngle")} data-testid="rfq-tilt" />
                          <Field label="Inter-row Pitch / Gap — optional" value={form.rowPitch}
                            onChange={upd("rowPitch")} data-testid="rfq-pitch" />
                        </div>
                      </div>
                    )}
                    {step === 2 && (
                      <div className="space-y-5">
                        <div>
                          <p className="text-sm font-semibold text-helios-slate mb-3">Cleaning Preference</p>
                          <OptionGroup options={CLEAN_PREFS} value={form.cleaningPreference}
                            onChange={(v) => set("cleaningPreference", v)} testidPrefix="rfq-clean" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-helios-slate mb-3">Robot Line of Interest</p>
                          <OptionGroup options={ROBOT_LINES} value={form.robotLine}
                            onChange={(v) => set("robotLine", v)} testidPrefix="rfq-robot" />
                        </div>
                      </div>
                    )}
                    {step === 3 && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <Field label="Organization / EPC Name" value={form.organization}
                            onChange={upd("organization")} data-testid="rfq-org" />
                          <Field label="Contact Person" value={form.contactPerson}
                            onChange={upd("contactPerson")} data-testid="rfq-person" />
                        </div>
                        <div>
                          <span className="block text-xs font-medium text-slate-500 mb-1.5">WhatsApp / Mobile Number</span>
                          <div className="flex gap-2">
                            <select value={form.countryCode} onChange={upd("countryCode")}
                              className="rounded-lg border border-helios-line bg-white px-2 text-sm h-10 focus:outline-none focus:ring-2 focus:ring-helios-cyan"
                              data-testid="rfq-country-code">
                              {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                            </select>
                            <Input className="rounded-lg border-helios-line flex-1" placeholder="98765 43210"
                              value={form.mobile} onChange={upd("mobile")} data-testid="rfq-mobile" />
                          </div>
                        </div>
                        <Field label="Business Email" type="email" value={form.email}
                          onChange={upd("email")} data-testid="rfq-email" />
                        <div className="grid grid-cols-2 gap-4">
                          <Field label="Plant City" value={form.city} onChange={upd("city")} data-testid="rfq-city" />
                          <Field label="State" value={form.state} onChange={upd("state")} data-testid="rfq-state" />
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                <div className="flex items-center justify-between gap-3 mt-7">
                  {step > 1 ? (
                    <button onClick={() => setStep((s) => s - 1)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-helios-slate transition-colors"
                      data-testid="rfq-back">
                      <ArrowLeft size={15} /> Back
                    </button>
                  ) : <span />}
                  {step < 3 ? (
                    <Button onClick={next} className="btn-sheen rounded-full bg-helios-slate hover:bg-helios-slate/90 px-7 h-11" data-testid="rfq-next">
                      Continue <ArrowRight size={15} className="ml-1.5" />
                    </Button>
                  ) : (
                    <Button onClick={submit} disabled={loading}
                      className="btn-sheen rounded-full bg-helios-cyan hover:bg-helios-cyan/90 px-7 h-11" data-testid="rfq-submit">
                      {loading ? <Loader2 className="animate-spin" size={17} /> : <>Submit Inquiry <ArrowRight size={15} className="ml-1.5" /></>}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
